/**
 * Bucket records by year, then by a group inside each year, for the
 * year-tab archives (completed courses, earned certificates).
 *
 *   bucketByYear(items, {date, groupKey, groupTitle})
 *   -> [{key, label, count, groups: [{key, title, items}]}]
 *
 * Years are newest first with undated records last under "Undated"; within
 * a year, groups keep first-seen order except 'other', which trails. Items
 * keep the order they came in (callers pass newest-first lists). The
 * current year is always present, possibly empty.
 */
export function bucketByYear(items, {date, groupKey, groupTitle}) {
    const yearMap = {};
    const years = [];
    // The current year is always a tab (even with nothing in it yet) so
    // every archive opens on "this year" like the Conference Center does
    const current = new Date().getFullYear();
    yearMap[current] = {key: current, label: String(current), count: 0, groups: [], groupMap: {}};
    years.push(yearMap[current]);
    for (const item of items || []) {
        const raw = date(item);
        const d = raw ? new Date(raw) : null;
        const key = d && !isNaN(d) ? d.getFullYear() : 'undated';
        if (!yearMap[key]) {
            yearMap[key] = {key, label: key === 'undated' ? 'Undated' : String(key), count: 0, groups: [], groupMap: {}};
            years.push(yearMap[key]);
        }
        const year = yearMap[key];
        const gk = groupKey(item);
        if (!year.groupMap[gk]) {
            year.groupMap[gk] = {key: gk, title: groupTitle(item), items: []};
            year.groups.push(year.groupMap[gk]);
        }
        year.groupMap[gk].items.push(item);
        year.count++;
    }
    years.sort((a, b) => {
        if (a.key === 'undated') return 1;
        if (b.key === 'undated') return -1;
        return b.key - a.key;
    });
    for (const year of years) {
        year.groups.sort((a, b) => (a.key === 'other') - (b.key === 'other'));
        delete year.groupMap;
    }
    return years;
}

/**
 * Which year tab to open first: the current year when it is in the list,
 * else the most recent past year, else the first entry. `years` are
 * numeric keys or {key} objects, newest first.
 */
export function defaultYearKey(years) {
    const keys = (years || []).map(y => (y && typeof y === 'object') ? y.key : y);
    if (!keys.length) return null;
    const current = new Date().getFullYear();
    if (keys.includes(current)) return current;
    const past = keys.find(k => typeof k === 'number' && k <= current);
    return past !== undefined ? past : keys[0];
}
